import {
  DataPreparationOverlayElementsContext,
  useDataPreparationOverlayElementsInitialState,
} from "@/data-preparation-tool/data-preparation-overlay-context";
import { useScreenSizeCheck } from "@/data-preparation-tool/hooks/use-screen-size-check";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { selectCurrentUser } from "@/store/user-selectors";
import { redirectToViewer } from "@/utils/redirects";
import { LoadingScreen } from "@faro-lotv/flat-ui";
import { useTranslate } from "@faro-lotv/foreign-observers";
import { GUID, assert } from "@faro-lotv/foundation";
import {
  IElementType,
  isIElementSectionDataSession,
  isIElementTimeseriesDataSession,
} from "@faro-lotv/ielement-types";
import { selectIElement, selectProjectName } from "@faro-lotv/project-source";
import { useTheme } from "@mui/material";
import type { JSX } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLoadIElements } from "../../hooks/use-load-ielements";
import { useAppSelector } from "../../store/store-hooks";
import {
  AlignmentOverlayContext,
  useAlignmentOverlayInitialState,
} from "../common/alignment-overlay-context";
import {
  PairwiseRegistrationUi,
  PairwiseRegistrationUiProps,
} from "./pairwise-registration-ui";

/**
 * @returns Entry point of the pairwise registration workflow.
 * This tool allows the user to visually & automatically align two point clouds.
 *
 * Shows an error page when being used on a small screen.
 */
export function PairwiseRegistrationPage(): JSX.Element | null {
  // Extract all needed URL parameters
  const { projectId } = useParams();
  assert(projectId, "No project ID provided");

  const [searchParams] = useSearchParams();
  const refCloudId = searchParams.get("refCloudId");
  const modelCloudId = searchParams.get("modelCloudId");
  const rawDashboardRedirect = searchParams.get("dashboardRedirect");
  assert(refCloudId, "No reference cloud ID provided");
  assert(modelCloudId, "No model cloud ID provided");
  const dashboardRedirect = rawDashboardRedirect
    ? decodeURIComponent(rawDashboardRedirect)
    : undefined;

  const currentUser = useAppSelector(selectCurrentUser);
  const theme = useTheme();

  // The tool doesn't work on small screens
  // Show an error page instead and ask the user to use a tablet or desktop PC
  useScreenSizeCheck({
    threshold: theme.breakpoints.values.md,
    handleRedirect: () => redirectToViewer(projectId),
    label: "Return to project",
  });

  const manualAlignmentOverlay = useAlignmentOverlayInitialState();

  const translate = useTranslate();

  const pageTitle = translate("Pairwise Registration");
  const projectName = useAppSelector(selectProjectName);
  const title = projectName ? `${pageTitle} - ${projectName}` : pageTitle;
  useDocumentTitle(title);

  return (
    <AlignmentOverlayContext value={manualAlignmentOverlay}>
      <PairwiseRegistrationLoading
        modelCloudId={modelCloudId}
        refCloudId={refCloudId}
        dashboardRedirect={dashboardRedirect}
        currentUser={currentUser}
      />
    </AlignmentOverlayContext>
  );
}

type PairwiseRegistrationLoadingProps = {
  /** The ID of the Section.DataSession used as a reference in the registration. */
  refCloudId: GUID;

  /** The ID of the Section.DataSession which is being adjusted during the registration. */
  modelCloudId: GUID;

  /** The dashboard path to redirect to. */
  dashboardRedirect: PairwiseRegistrationUiProps["dashboardRedirect"];

  /** The currently logged-in user. */
  currentUser?: PairwiseRegistrationUiProps["currentUser"];
};

/** @returns Wrapper for the pairwise registration view, ensuring that all needed elements are loaded. */
function PairwiseRegistrationLoading({
  refCloudId,
  modelCloudId,
  dashboardRedirect,
  currentUser,
}: PairwiseRegistrationLoadingProps): JSX.Element {
  // Load the elements required for the view
  const isLoading = useLoadIElements([
    // Load ancestors (needed for world transforms and sheet)
    {
      descendantIds: [refCloudId, modelCloudId],
    },
    // Load descendants (needed for point cloud streams)
    {
      ancestorIds: [refCloudId, modelCloudId],
      types: [IElementType.section, IElementType.pointCloudStream],
    },
  ]);
  const refCloudDataSession = useAppSelector(selectIElement(refCloudId));
  const modelCloudDataSession = useAppSelector(selectIElement(modelCloudId));
  const timeSeries = useAppSelector(
    selectIElement(refCloudDataSession?.parentId),
  );

  const overlayElementsInitialValue =
    useDataPreparationOverlayElementsInitialState();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Validate the elements
  assert(refCloudDataSession, "Reference cloud not found");
  assert(modelCloudDataSession, "Model cloud not found");
  assert(
    isIElementSectionDataSession(modelCloudDataSession),
    "Model point cloud id does not reference a data session.",
  );
  assert(
    isIElementSectionDataSession(refCloudDataSession),
    "Reference point cloud id does not reference a data session.",
  );

  assert(timeSeries, "Time series not found");
  assert(
    isIElementTimeseriesDataSession(timeSeries),
    "Parent is not a valid time series",
  );

  assert(currentUser, "No current user available");

  return (
    <DataPreparationOverlayElementsContext value={overlayElementsInitialValue}>
      <PairwiseRegistrationUi
        refCloudDataSession={refCloudDataSession}
        modelCloudDataSession={modelCloudDataSession}
        dashboardRedirect={dashboardRedirect}
        currentUser={currentUser}
      />
    </DataPreparationOverlayElementsContext>
  );
}
